import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Trash2, Power, Shield, Users, Loader } from 'lucide-react';
import { api } from '@/services/api';
import clsx from 'clsx';

interface GuestUser {
  id: string;
  username: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
}

export default function AdminPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<GuestUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toggle listener — close settings panel when admin opens
  useEffect(() => {
    const handler = () => {
      setIsOpen((prev) => {
        if (!prev) {
          // Opening admin — close settings panel
          window.dispatchEvent(new CustomEvent('jarvis-close-settings'));
        }
        return !prev;
      });
    };
    window.addEventListener('jarvis-admin-toggle', handler);
    return () => window.removeEventListener('jarvis-admin-toggle', handler);
  }, []);

  const onClose = () => setIsOpen(false);

  // Create form state
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ users: GuestUser[]; total: number }>('/admin/users');
      setUsers(data.users);
    } catch {
      setError('Failed to load users');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) loadUsers();
  }, [isOpen, loadUsers]);

  const handleCreate = async () => {
    if (!newUsername || !newEmail || !newPassword) {
      setError('All fields are required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await api.post('/admin/users', {
        username: newUsername,
        email: newEmail,
        full_name: newFullName || null,
        password: newPassword,
      });
      setNewUsername('');
      setNewEmail('');
      setNewFullName('');
      setNewPassword('');
      setShowCreateForm(false);
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create user');
    }
    setCreating(false);
  };

  const handleDelete = async (userId: string) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      await loadUsers();
    } catch {
      setError('Failed to delete user');
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      await loadUsers();
    } catch {
      setError('Failed to update role');
    }
  };

  const handleToggleActive = async (userId: string) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-active`);
      await loadUsers();
    } catch {
      setError('Failed to update user');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 panel-backdrop"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, x: -40, scale: 0.97 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -40, scale: 0.97 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed left-5 top-20 bottom-24 w-full max-w-md z-50 glass-heavy hud-clip flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-jarvis-blue/10">
              <h2 className="font-display text-sm font-semibold tracking-wider text-jarvis-blue flex items-center gap-2">
                <Users size={16} />
                User Management
              </h2>
              <button onClick={onClose} className="text-gray-500 hover:text-jarvis-blue transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mx-4 mt-3 px-3 py-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 hud-clip-sm">
                {error}
              </div>
            )}

            {/* Create Button / Form */}
            <div className="p-4 border-b border-jarvis-blue/30">
              {showCreateForm ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus size={14} className="text-jarvis-blue" />
                    <span className="text-xs font-mono text-jarvis-blue tracking-wider">NEW GUEST ACCOUNT</span>
                  </div>

                  <input
                    type="text"
                    placeholder="Username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-mono bg-white/[0.03] border border-jarvis-blue/30 text-gray-200 placeholder:text-gray-600 hud-clip-sm focus:border-jarvis-blue/30 focus:outline-none"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-mono bg-white/[0.03] border border-jarvis-blue/30 text-gray-200 placeholder:text-gray-600 hud-clip-sm focus:border-jarvis-blue/30 focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Full Name (optional)"
                    value={newFullName}
                    onChange={(e) => setNewFullName(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-mono bg-white/[0.03] border border-jarvis-blue/30 text-gray-200 placeholder:text-gray-600 hud-clip-sm focus:border-jarvis-blue/30 focus:outline-none"
                  />
                  <input
                    type="password"
                    placeholder="Password (min 6 chars)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-mono bg-white/[0.03] border border-jarvis-blue/30 text-gray-200 placeholder:text-gray-600 hud-clip-sm focus:border-jarvis-blue/30 focus:outline-none"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowCreateForm(false); setError(null); }}
                      className="flex-1 px-3 py-2 text-xs font-mono text-gray-400 border border-jarvis-blue/30 hover:border-white/[0.12] transition-colors hud-clip-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreate}
                      disabled={creating}
                      className="flex-1 px-3 py-2 text-xs font-mono text-jarvis-blue border border-jarvis-blue/20 bg-jarvis-blue/[0.08] hover:bg-jarvis-blue/[0.15] transition-colors hud-clip-sm flex items-center justify-center gap-1.5"
                    >
                      {creating ? <Loader size={12} className="animate-spin" /> : <UserPlus size={12} />}
                      Create
                    </button>
                  </div>

                  <p className="text-[10px] text-gray-600 font-mono">
                    Guest accounts can chat with JARVIS but cannot access your contacts, calendar, health data, camera, or any private information.
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full jarvis-button py-2.5 px-4 text-sm font-medium flex items-center justify-center gap-2 tracking-wide"
                >
                  <UserPlus size={16} />
                  Create Guest Account
                </button>
              )}
            </div>

            {/* User List */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader size={20} className="animate-spin text-jarvis-blue" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8">
                  <Users size={28} className="mx-auto text-gray-600 mb-2" />
                  <p className="text-xs text-gray-500">No users yet.</p>
                </div>
              ) : (
                users.map((user) => (
                  <div
                    key={user.id}
                    className={clsx(
                      'p-3 hud-clip-sm border transition-all',
                      user.is_active
                        ? 'border-jarvis-blue/30 bg-white/[0.02]'
                        : 'border-red-500/10 bg-red-500/[0.03] opacity-60'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-200">{user.username}</span>
                          <span
                            className={clsx(
                              'text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded',
                              user.role === 'owner'
                                ? 'bg-jarvis-blue/10 text-jarvis-blue border border-jarvis-blue/20'
                                : 'bg-white/[0.04] text-gray-500 border border-jarvis-blue/30'
                            )}
                          >
                            {user.role.toUpperCase()}
                          </span>
                          {!user.is_active && (
                            <span className="text-[9px] font-mono text-red-400">DISABLED</span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 font-mono mt-0.5 truncate">{user.email}</p>
                        {user.full_name && (
                          <p className="text-[11px] text-gray-600 mt-0.5">{user.full_name}</p>
                        )}
                      </div>

                      {/* Actions (not for owner) */}
                      {user.role !== 'owner' && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <select
                            value={user.role}
                            onChange={(e) => handleChangeRole(user.id, e.target.value)}
                            className="text-[9px] font-mono bg-transparent border border-jarvis-blue/30 text-gray-400 px-1 py-0.5 focus:outline-none focus:border-jarvis-blue/50"
                          >
                            <option value="guest">GUEST</option>
                            <option value="secondary">SECONDARY</option>
                          </select>
                          <button
                            onClick={() => handleToggleActive(user.id)}
                            className={clsx(
                              'p-1.5 hud-clip-sm transition-colors',
                              user.is_active
                                ? 'text-gray-500 hover:text-yellow-400 hover:bg-yellow-500/10'
                                : 'text-green-500 hover:bg-green-500/10'
                            )}
                            title={user.is_active ? 'Disable account' : 'Enable account'}
                          >
                            <Power size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-1.5 hud-clip-sm text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Delete account"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-jarvis-blue/30">
              <div className="flex items-center gap-2">
                <Shield size={12} className="text-jarvis-blue/40" />
                <p className="text-[10px] text-gray-600 font-mono">
                  Guest access: chat only. No private data, camera, or admin access.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
